import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Session } from "@/entities/user";
import { api, ApiError, readCsrfToken, refreshSession, sessionKey } from "@/shared/api";

async function fetchMe(): Promise<Session> {
  const { data } = await api.GET("/auth/me");
  if (!data) throw new Error("Empty /auth/me response");
  return data;
}

/**
 * Resolves to the session, or null when nobody is signed in. A 401 on bootstrap may just mean the
 * 15-minute access cookie lapsed while the refresh cookie is still good, so try one refresh first.
 */
export async function fetchSession(): Promise<Session | null> {
  try {
    return await fetchMe();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    // No csrf cookie means this browser never signed in (or logged out): nothing to refresh.
    if (!readCsrfToken() || !(await refreshSession())) return null;
    try {
      return await fetchMe();
    } catch (retryError) {
      if (retryError instanceof ApiError && retryError.status === 401) return null;
      throw retryError;
    }
  }
}

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: fetchSession,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      await api.POST("/auth/login", { body: credentials });
      // /auth/me also carries the csrf token and expiry the login response does not.
      const session = await fetchMe();
      queryClient.setQueryData(sessionKey, session);
      return session;
    },
  });
}

/** Second half of "Sign in with Zoho": the ticket from the redirect plus this tab's verifier become a session. */
export function useZohoFinish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { ticket: string; verifier: string }) => {
      await api.POST("/auth/zoho/finish", { body });
      const session = await fetchMe();
      queryClient.setQueryData(sessionKey, session);
      return session;
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await api.POST("/auth/logout");
      } catch {
        // Even if the call fails the local session is dropped; the cookies expire on their own.
      }
    },
    onSettled: () => {
      queryClient.clear();
      queryClient.setQueryData(sessionKey, null);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { current_password: string; new_password: string }) => {
      await api.POST("/auth/change-password", { body });
    },
  });
}
